import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { useLang, t } from "@/context/LangContext";
import { Send, Bot, User, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const SUGGESTIONS_ID = [
  "Analisa fundamental BBCA",
  "Apakah NVDA overvalued sekarang?",
  "Bandingkan BBRI vs BMRI",
  "Jelaskan cara membaca laporan keuangan",
];
const SUGGESTIONS_EN = [
  "Analyze AAPL fundamentals",
  "Is NVDA overvalued right now?",
  "Compare Coca-Cola vs Pepsi",
  "Explain how to read financial statements",
];

const AIChat = () => {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const [sessionId] = useState(() => "sess-" + Math.random().toString(36).slice(2, 10));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [stockSymbol] = useState(params.get("symbol") || null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (stockSymbol) {
      setInput(lang === "id" ? `Berikan analisa lengkap ${stockSymbol}` : `Give me a full analysis of ${stockSymbol}`);
    }
  }, [stockSymbol, lang]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (msgText) => {
    const text = msgText ?? input;
    if (!text.trim() || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, stock_symbol: stockSymbol, language: lang }),
      });
      if (!resp.ok || !resp.body) throw new Error("Stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        // Parse SSE lines
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") break;
            if (data.startsWith("[ERROR]")) {
              acc += "\n\n⚠️ " + data;
            } else {
              acc += data;
            }
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: acc };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t(lang, "Terjadi kesalahan. Coba lagi.", "Error occurred. Please retry.") };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = lang === "id" ? SUGGESTIONS_ID : SUGGESTIONS_EN;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12" data-testid="ai-chat-page">
      <div className="overline text-white/40 mb-3">Powered by Claude Sonnet 4.5</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2 flex items-center gap-3">
        <Bot size={40} strokeWidth={1.5} /> AI Analyst
      </h1>
      <p className="text-white/60 mb-8">
        {t(lang, "Tanya apapun tentang saham IDX, US, IPO, atau strategi investasi.", "Ask anything about IDX, US stocks, IPOs, or investing strategy.")}
      </p>

      <div className="border border-white/10 rounded-2xl min-h-[500px] flex flex-col bg-[#0d0d0d] scanlines">
        <div className="flex-1 p-6 overflow-y-auto space-y-6" data-testid="chat-messages" style={{ maxHeight: "60vh" }}>
          {messages.length === 0 && (
            <div className="text-center py-16">
              <Bot size={48} strokeWidth={1} className="mx-auto text-white/30 mb-4" />
              <div className="text-white/40 mb-6">{t(lang, "Mulai percakapan...", "Start a conversation...")}</div>
              <div className="grid sm:grid-cols-2 gap-2 max-w-xl mx-auto">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    data-testid={`suggestion-${i}`}
                    className="text-left text-sm border border-white/10 rounded-lg p-3 hover:border-white/30 hover:bg-white/5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`msg-${i}`} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "user" ? "bg-white text-black" : "bg-white/10"}`}>
                {m.role === "user" ? <User size={14} /> : <Bot size={14} />}
              </div>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-white text-black" : "bg-white/5 text-white/90"}`}>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content || (loading && i === messages.length - 1 ? "…" : "")}</div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-white/10 p-4 flex gap-3">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder={t(lang, "Ketik pertanyaan Anda... (Enter untuk kirim)", "Ask a question... (Enter to send)")}
            className="bg-white/5 border-white/10 resize-none min-h-[52px] max-h-[120px]"
            data-testid="chat-input"
            disabled={loading}
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            data-testid="send-btn"
            className="bg-white text-black rounded-full px-5 flex items-center justify-center hover:bg-white/90 disabled:opacity-40 transition-colors"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>

      <div className="text-xs text-white/40 mt-4">
        {t(lang, "⚠️ Konten AI bukan saran keuangan. Selalu lakukan riset sendiri.", "⚠️ AI content is not financial advice. Always do your own research.")}
      </div>
    </div>
  );
};

export default AIChat;
